import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Anchor, Plus, Trash2, ArrowLeft } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyItem = { description: "", quantity: 1, unit_price: 0, tax_rate: 18 };
const emptyCargo = { description: "", container_no: "", container_type: "20ft", weight_kg: 0, quantity: 1 };

const blank = (type) => ({
  invoice_type: type || "shipping",
  currency: "INR",
  issue_date: today(),
  due_date: plus(15),
  consignor_name: "", consignor_address: "",
  consignee_name: "", consignee_address: "", consignee_email: "",
  vessel_name: "", voyage_no: "", bl_no: "",
  port_of_loading: "", port_of_discharge: "",
  cargo: type === "shipping" ? [{ ...emptyCargo }] : [],
  items: [{ ...emptyItem }],
  tax_label: "GST",
  discount: 0,
  notes: "",
  status: "draft",
});

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const type = params.get("type") || "shipping";
  const [form, setForm] = useState(blank(type));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const { data } = await api.get(`/invoices/${id}`);
          setForm({ ...blank(data.invoice_type), ...data });
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      })();
    }
  }, [id]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };

  const updateCargo = (idx, patch) => {
    setForm((f) => {
      const cargo = [...f.cargo];
      cargo[idx] = { ...cargo[idx], ...patch };
      return { ...f, cargo };
    });
  };

  const addItem = () => update({ items: [...form.items, { ...emptyItem }] });
  const removeItem = (i) => update({ items: form.items.filter((_, idx) => idx !== i) });
  const addCargo = () => update({ cargo: [...form.cargo, { ...emptyCargo }] });
  const removeCargo = (i) => update({ cargo: form.cargo.filter((_, idx) => idx !== i) });

  // Totals
  let subtotal = 0, taxTotal = 0;
  for (const it of form.items) {
    const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
    subtotal += line;
    taxTotal += line * Number(it.tax_rate || 0) / 100;
  }
  const grand = subtotal + taxTotal - Number(form.discount || 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount: Number(form.discount) || 0,
        items: form.items.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
        cargo: (form.cargo || []).map((c) => ({
          ...c,
          weight_kg: Number(c.weight_kg) || 0,
          quantity: Number(c.quantity) || 1,
        })),
      };
      if (!payload.consignee_email) delete payload.consignee_email;
      const res = id
        ? await api.put(`/invoices/${id}`, payload)
        : await api.post("/invoices", payload);
      toast.success(id ? "Invoice updated" : "Invoice created");
      navigate(`/invoices/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const isShipping = form.invoice_type === "shipping";

  return (
    <div className="space-y-6" data-testid="invoice-form-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/invoices")} className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-2 flex items-center gap-1" data-testid="back-to-invoices">
            <ArrowLeft className="h-3 w-3" /> Back to invoices
          </button>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
            {isShipping ? "Freight Invoice" : "Service Invoice"}
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight">
            {id ? "Edit invoice" : "Generate invoice"}
          </h1>
        </div>
        <div className="flex gap-2">
          <Select value={form.invoice_type} onValueChange={(v) => update({ invoice_type: v, cargo: v === "shipping" ? (form.cargo.length ? form.cargo : [{ ...emptyCargo }]) : [] })}>
            <SelectTrigger className="rounded-sm h-11 w-44 border-zinc-300" data-testid="inv-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shipping">Freight</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={saving} data-testid="inv-save" className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-6">
            {saving ? "Saving…" : (id ? "Update" : "Create invoice")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Invoice details">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => update({ currency: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="inv-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["INR", "USD", "AED", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tax label">
                <Select value={form.tax_label} onValueChange={(v) => update({ tax_label: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["GST", "VAT", "None"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="inv-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["draft", "sent", "paid", "overdue"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Issue date">
                <Input type="date" value={form.issue_date} onChange={(e) => update({ issue_date: e.target.value })} className="rounded-sm" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={(e) => update({ due_date: e.target.value })} className="rounded-sm" />
              </Field>
            </div>
          </Card>

          <Card title="Parties">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Consignee name">
                <Input value={form.consignee_name} onChange={(e) => update({ consignee_name: e.target.value })} className="rounded-sm" data-testid="inv-consignee-name" placeholder="e.g. Acme Trading Co." />
              </Field>
              <Field label="Consignee email">
                <Input type="email" value={form.consignee_email} onChange={(e) => update({ consignee_email: e.target.value })} className="rounded-sm" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Consignee address">
                  <Textarea rows={2} value={form.consignee_address} onChange={(e) => update({ consignee_address: e.target.value })} className="rounded-sm" />
                </Field>
              </div>
              {isShipping && (
                <>
                  <Field label="Consignor name">
                    <Input value={form.consignor_name} onChange={(e) => update({ consignor_name: e.target.value })} className="rounded-sm" />
                  </Field>
                  <Field label="Consignor address">
                    <Input value={form.consignor_address} onChange={(e) => update({ consignor_address: e.target.value })} className="rounded-sm" />
                  </Field>
                </>
              )}
            </div>
          </Card>

          {isShipping && (
            <Card title="Shipment">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Field label="Vessel name"><Input value={form.vessel_name} onChange={(e) => update({ vessel_name: e.target.value })} className="rounded-sm" /></Field>
                <Field label="Voyage no"><Input value={form.voyage_no} onChange={(e) => update({ voyage_no: e.target.value })} className="rounded-sm font-mono" /></Field>
                <Field label="B/L no"><Input value={form.bl_no} onChange={(e) => update({ bl_no: e.target.value })} className="rounded-sm font-mono" /></Field>
                <Field label="Port of loading"><Input value={form.port_of_loading} onChange={(e) => update({ port_of_loading: e.target.value })} className="rounded-sm" /></Field>
                <Field label="Port of discharge"><Input value={form.port_of_discharge} onChange={(e) => update({ port_of_discharge: e.target.value })} className="rounded-sm" /></Field>
              </div>
              <div className="border-t border-zinc-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Cargo / containers</Label>
                  <Button size="sm" variant="outline" onClick={addCargo} data-testid="add-cargo" className="rounded-sm h-8 font-mono text-[10px] uppercase tracking-widest">
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.cargo.map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`cargo-row-${i}`}>
                      <Input value={c.container_no} onChange={(e) => updateCargo(i, { container_no: e.target.value })} placeholder="Container No" className="col-span-3 rounded-sm font-mono text-sm" />
                      <Select value={c.container_type} onValueChange={(v) => updateCargo(i, { container_type: v })}>
                        <SelectTrigger className="col-span-2 rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["20ft", "40ft", "40HC", "Reefer", "Tank"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={c.description} onChange={(e) => updateCargo(i, { description: e.target.value })} placeholder="Description" className="col-span-4 rounded-sm" />
                      <Input type="number" value={c.quantity} onChange={(e) => updateCargo(i, { quantity: e.target.value })} placeholder="Qty" className="col-span-1 rounded-sm font-mono" />
                      <Input type="number" value={c.weight_kg} onChange={(e) => updateCargo(i, { weight_kg: e.target.value })} placeholder="Weight (kg)" className="col-span-1 rounded-sm font-mono" />
                      <Button size="sm" variant="ghost" onClick={() => removeCargo(i)} className="col-span-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          <Card title={isShipping ? "Freight charges" : "Service items"}>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Line items</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item" className="rounded-sm h-8 font-mono text-[10px] uppercase tracking-widest">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`item-row-${i}`}>
                  <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" className="col-span-6 rounded-sm" />
                  <Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} placeholder="Qty" className="col-span-1 rounded-sm font-mono" />
                  <Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} placeholder="Price" className="col-span-2 rounded-sm font-mono" />
                  <Input type="number" value={it.tax_rate} onChange={(e) => updateItem(i, { tax_rate: e.target.value })} placeholder="Tax %" className="col-span-2 rounded-sm font-mono" />
                  <Button size="sm" variant="ghost" onClick={() => removeItem(i)} className="col-span-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Notes">
            <Textarea rows={3} value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Payment terms, banking details, etc." className="rounded-sm" />
          </Card>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 self-start">
          <Card title="Summary">
            <div className="space-y-3 font-mono">
              <Row label="Subtotal" value={formatMoney(subtotal, form.currency)} />
              <Row label={`${form.tax_label}`} value={formatMoney(taxTotal, form.currency)} />
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Discount</span>
                <Input
                  type="number"
                  value={form.discount}
                  onChange={(e) => update({ discount: e.target.value })}
                  className="w-28 h-8 text-right font-mono rounded-sm"
                />
              </div>
              <div className="pt-3 mt-3 border-t-2 border-[#09090B] flex items-center justify-between">
                <span className="font-heading font-bold uppercase tracking-tight">Grand total</span>
                <span className="font-heading font-black text-xl">{formatMoney(grand, form.currency)}</span>
              </div>
            </div>
            <div className="mt-6 p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-xs text-zinc-600 leading-relaxed">
              Once created, you can download a print-ready PDF or share with the consignee.
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm">
      <div className="px-6 py-4 border-b border-zinc-200">
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">Section</div>
        <h3 className="font-heading text-lg font-bold tracking-tight">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
